import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { data, portal } from "@aawaz/api-client";
import {
  Badge, Button, Card, Drawer, EmptyState, ErrorBanner, Icon, Input, Modal, MonthPicker, Notice, PageHeader, Ring, SkeletonRows, Stat, Table, fmtDate,
  fmtPeriod, maskPhone,
} from "@aawaz/ui";
import { Link } from "react-router-dom";
import { inr, lastMonth } from "./Home";
import { useSession } from "../session";

interface Check { key: string; ok: boolean; count: number; text: string; action: string | null; amount?: string }
interface DeskRow { client_org_id: string; client: string; score: number; itc: { at_risk: string; unclaimed: string; has_2b: boolean }; open: Check[] }
interface Invoice { number: string; date: string | null; value: string | null; tax: string | null }
interface Supplier { supplier_gstin: string; supplier_name: string | null; phone: string | null; itc_at_risk: string; invoices: Invoice[] }
interface Unclaimed { supplier_gstin: string; supplier_name: string | null; number: string; date: string | null; itc: string }
interface Detail { score: number; checks: Check[]; watch: { has_2b: boolean; itc_at_risk: string; itc_unclaimed: string; itc_matched: string;
  at_risk_without_tax: number; suppliers_at_risk: Supplier[]; unclaimed: Unclaimed[]; mismatches: { number: string; value: string; value_2b: string }[] } }

/** GST desk: every client's return for one month. How ready it is, rupees of input tax credit at risk or
 *  unclaimed, what is missing, and the one-tap actions that close each gap (remind the client, nudge a supplier). */
export function GstDeskPage() {
  const s = useSession();
  const qc = useQueryClient();
  const [period, setPeriod] = useState(lastMonth());
  const [open, setOpen] = useState<DeskRow | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const desk = useQuery({ queryKey: ["ca-desk", period], queryFn: () =>
    data<{ data: DeskRow[] }>(portal.GET("/portal/v1/ca/desk", { params: { query: { period } } })) });
  const remind = useMutation({
    mutationFn: (ids: string[] | null) => data<{ data: { client_org_id: string; status: string }[] }>(
      portal.POST("/portal/v1/ca/chase:send", { body: { period, client_org_ids: ids } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ca-desk", period] }),
  });
  const rows = desk.data?.data ?? [];
  const sum = (k: "at_risk" | "unclaimed") => rows.reduce((n, r) => n + Number(r.itc[k] || 0), 0);
  const ready = rows.filter((r) => r.open.length === 0).length;
  const missingDocs = rows.filter((r) => r.open.some((c) => c.key === "documents" || c.key === "readable"));
  return (
    <>
      <PageHeader title="GST desk" description="How ready each client's return is, and the rupees of input tax credit at stake."
        actions={<MonthPicker aria-label="Month" value={period} onChange={(v) => v && setPeriod(v)} style={{ width: 160 }} />} />
      <ErrorBanner error={desk.error ?? remind.error} />
      <div className="ui-stats">
        <Stat label="ITC at risk" value={inr(sum("at_risk"))} tone={sum("at_risk") ? "red" : undefined} icon="alert"
              note="bills suppliers have not filed in GSTR-2B" />
        <Stat label="ITC not yet claimed" value={inr(sum("unclaimed"))} tone={sum("unclaimed") ? "amber" : undefined} icon="zap"
              note="in GSTR-2B, not in the books" />
        <Stat label="Ready to file" value={`${ready} / ${rows.length}`} icon="check" note="nothing left open" />
        <Stat label="Waiting on clients" value={missingDocs.length} icon="message" note="documents missing or unreadable" />
      </div>
      <Card title={`Clients · ${fmtPeriod(period)}`} actions={s.can("review") && missingDocs.length ? (
        <Button onClick={() => setPreviewing(true)}>Remind all missing</Button>) : null}>
        {remind.data ? <Notice tone="success">Reminders: {summarise(remind.data.data)}</Notice> : null}
        {desk.isLoading ? <SkeletonRows /> : rows.length === 0 ? (
          <EmptyState icon="building" title="No clients yet" action={<Link className="ui-btn ui-btn-primary" to="/clients">Add clients</Link>}>
            Add clients with their GSTINs to see how ready each return is.</EmptyState>
        ) : (
          <Table rows={rows} rowKey={(r) => r.client_org_id} onRowClick={(r) => setOpen(r)} columns={[
            { key: "c", header: "Client", render: (r) => (
              <div className="ui-cell-main"><Ring value={r.score} size={36} stroke={4} /><span className="ui-cell-title">{r.client}</span></div>) },
            { key: "r", header: "ITC at risk", render: (r) => r.itc.has_2b ? <strong className={Number(r.itc.at_risk) ? "ui-text-red" : undefined}>{inr(r.itc.at_risk)}</strong>
              : <span className="ui-hint">2B not in</span> },
            { key: "u", header: "Unclaimed", render: (r) => r.itc.has_2b ? inr(r.itc.unclaimed) : "—" },
            { key: "o", header: "What is open", render: (r) => r.open.length ? <span className="ui-hint">{r.open.map((c) => c.text).join(" · ")}</span>
              : <Badge value="ready" tone="green" /> },
          ]} />
        )}
      </Card>
      <Drawer open={!!open} width={860} title={open ? `${open.client} · ${fmtPeriod(period)}` : ""} onClose={() => setOpen(null)}
              actions={open ? <Link className="ui-btn ui-btn-sm" to={`/clients/${open.client_org_id}`}>Client page</Link> : null}>
        {open ? <ClientDesk clientId={open.client_org_id} period={period} canAct={s.can("review")} onRemind={() => remind.mutate([open.client_org_id])}
                            reminding={remind.isPending} /> : null}
      </Drawer>
      <Modal open={previewing} size="lg" title="Reminders to send" icon={<Icon name="send" size={20} />}
             subtitle={`For ${fmtPeriod(period)}. Each client gets one WhatsApp message listing what is missing.`} onClose={() => setPreviewing(false)}>
        {previewing ? <RemindPreview period={period} busy={remind.isPending} onClose={() => setPreviewing(false)}
                                     onSend={(ids) => remind.mutate(ids, { onSettled: () => setPreviewing(false) })} /> : null}
      </Modal>
    </>
  );
}

interface ChaseRow { client_org_id: string; client: string; missing_text: string; unreadable: number; to: string | null;
  sent_at: string | null; failed: { at: string; error: string | null } | null }

/** Before reminders go out: who gets one, on which number, asking for what. Nothing is sent until the button. */
function RemindPreview({ period, busy, onSend, onClose }: { period: string; busy: boolean; onSend: (ids: string[]) => void; onClose: () => void }) {
  const q = useQuery({ queryKey: ["ca-chase-preview", period], queryFn: () =>
    data<{ data: ChaseRow[] }>(portal.GET("/portal/v1/ca/chase/preview", { params: { query: { period } } })) });
  const rows = q.data?.data ?? [];
  const going = rows.filter((r) => r.to && !r.sent_at);
  return (
    <>
      <ErrorBanner error={q.error} />
      {q.isLoading ? <SkeletonRows rows={4} /> : rows.length === 0 ? <p className="ui-hint" style={{ margin: 0 }}>Nobody has documents missing for this month.</p> : (
        <Table rows={rows} rowKey={(r) => r.client_org_id} columns={[
          { key: "c", header: "Client", render: (r) => <span className="ui-cell-title">{r.client}</span> },
          { key: "w", header: "Asked for", render: (r) => <span className="ui-hint">{[r.missing_text, r.unreadable ? `clearer photos of ${r.unreadable} bill${r.unreadable === 1 ? "" : "s"}` : ""].filter(Boolean).join("; ")}</span> },
          { key: "t", header: "Goes to", render: (r) => r.sent_at ? <span className="ui-hint">Already reminded {fmtDate(r.sent_at)}</span>
            : !r.to ? <span className="ui-text-red">No WhatsApp number on this client</span>
            : <><span className="ui-mono">{maskPhone(r.to)}</span>{r.failed ? <div className="ui-hint">Last try was not delivered{r.failed.error ? `: ${WHY_NOT_SENT[r.failed.error] ?? r.failed.error}` : ""}</div> : null}</> },
        ]} />
      )}
      <div className="ui-dialog-actions">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" icon="send" busy={busy} disabled={going.length === 0} onClick={() => onSend(going.map((r) => r.client_org_id))}>
          Send {going.length} reminder{going.length === 1 ? "" : "s"}</Button>
      </div>
    </>
  );
}

/** What happened to each reminder: a refused one is not counted as sent. */
function summarise(rows: { status: string; error?: string | null }[]): string {
  const n = (...st: string[]) => rows.filter((r) => st.includes(r.status)).length;
  const refused = rows.filter((r) => ["rejected", "failed"].includes(r.status));
  const why = [...new Set(refused.map((r) => WHY_NOT_SENT[r.error ?? ""] ?? r.error).filter(Boolean))].join("; ");
  return [`${n("accepted", "running", "succeeded")} sent`, n("already_sent") ? `${n("already_sent")} already reminded` : "",
          n("no_whatsapp") ? `${n("no_whatsapp")} without a WhatsApp number` : "",
          refused.length ? `${refused.length} not sent${why ? ` (${why})` : ""}` : ""].filter(Boolean).join(", ");
}
const WHY_NOT_SENT: Record<string, string> = {
  OUTSIDE_SERVICE_WINDOW: "the client has not written in the last 24 hours and no approved template is set",
  WHATSAPP_132001: "the reminder template is not approved in this language",
  WHATSAPP_132000: "the reminder template does not match what WhatsApp approved",
  WHATSAPP_131026: "the number is not on WhatsApp",
  WHATSAPP_190: "the WhatsApp token has expired",
};

/** One client's return for one month: score, ITC figures, the checklist and the suppliers to nudge. Also used on the client page. */
export function ClientDesk({ clientId, period, canAct, onRemind, reminding }: {
  clientId: string; period: string; canAct: boolean; onRemind: () => void; reminding: boolean;
}) {
  const qc = useQueryClient();
  const d = useQuery({ queryKey: ["ca-readiness", clientId, period], queryFn: () =>
    data<Detail>(portal.GET("/portal/v1/ca/readiness/{client_org_id}", { params: { path: { client_org_id: clientId }, query: { period } } })) });
  const [phones, setPhones] = useState<Record<string, string>>({});
  const nudge = useMutation({
    mutationFn: (sup: Supplier) => data<{ status: string; to?: string }>(portal.POST("/portal/v1/ca/itc/nudge", { body: {
      client_org_id: clientId, supplier_gstin: sup.supplier_gstin, period, phone: phones[sup.supplier_gstin] || sup.phone || null } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ca-readiness", clientId, period] }),
  });
  if (d.isLoading) return <SkeletonRows />;
  if (!d.data) return <ErrorBanner error={d.error} />;
  const w = d.data.watch;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <ErrorBanner error={nudge.error} />
      <div className="ui-row" style={{ gap: 18, alignItems: "center" }}>
        <Ring value={d.data.score} size={72} stroke={7} />
        <div><div className="ui-figure">{d.data.score >= 90 ? "Ready to file" : d.data.score >= 60 ? "Nearly there" : "Work to do"}</div>
          <span className="ui-hint">{d.data.checks.filter((c) => !c.ok).length} of {d.data.checks.length} checks open</span></div>
      </div>
      <div className="ui-stats" style={{ marginBottom: 0 }}>
        <Stat label="ITC at risk" icon="alert" value={w.has_2b ? inr(w.itc_at_risk) : "2B not in"} tone={Number(w.itc_at_risk) ? "red" : undefined}
              note={w.at_risk_without_tax ? `+ ${w.at_risk_without_tax} bill(s) with tax not known` : undefined} />
        <Stat label="Unclaimed" value={w.has_2b ? inr(w.itc_unclaimed) : "—"} />
        <Stat label="Matched in 2B" value={w.has_2b ? inr(w.itc_matched) : "—"} />
      </div>
      <Card title="Checklist">
        <div className="ui-steps">
          {d.data.checks.map((c) => (
            <div key={c.key} className={c.ok ? "ui-step ui-step-done" : "ui-step"}>
              <span className="ui-step-mark">{c.ok ? <Icon name="check" size={14} /> : null}</span>
              <div className="ui-step-body"><span className="ui-step-title">{c.text}</span></div>
              {!c.ok && c.action === "chase" && canAct ? <Button size="sm" icon="send" busy={reminding} onClick={onRemind}>Remind on WhatsApp</Button> : null}
            </div>
          ))}
        </div>
      </Card>
      {w.suppliers_at_risk.length ? (
        <Card title="Suppliers who have not filed">
          <Table rows={w.suppliers_at_risk} rowKey={(r) => r.supplier_gstin} columns={[
            { key: "s", header: "Supplier", render: (r) => <><strong>{r.supplier_name ?? r.supplier_gstin}</strong><div className="ui-hint ui-mono">{r.supplier_gstin}</div></> },
            { key: "i", header: "Invoices", render: (r) => <span className="ui-hint">{r.invoices.map((i) => `${i.number} ${inr(i.value)}`).join(", ")}</span> },
            { key: "a", header: "ITC at risk", render: (r) => inr(r.itc_at_risk) },
            { key: "n", header: "", render: (r) => canAct ? (
              <div className="ui-row" style={{ flexWrap: "nowrap" }}>
                {!r.phone ? <Input aria-label="Supplier WhatsApp" placeholder="Supplier WhatsApp" style={{ maxWidth: 160 }}
                                   value={phones[r.supplier_gstin] ?? ""} onChange={(e) => setPhones({ ...phones, [r.supplier_gstin]: e.target.value })} /> : null}
                <Button busy={nudge.isPending && nudge.variables?.supplier_gstin === r.supplier_gstin}
                        disabled={!r.phone && !(phones[r.supplier_gstin] || "").trim()}
                        onClick={() => nudge.mutate(r)}>Nudge on WhatsApp</Button>
              </div>) : null },
          ]} />
          {nudge.data ? <Notice tone="success">{nudge.data.status === "already_sent" ? "This supplier was already nudged for this month."
            : `Nudge sent${nudge.data.to ? ` to ${nudge.data.to}` : ""}.`}</Notice> : null}
        </Card>
      ) : null}
      {w.unclaimed.length ? (
        <Card title="In GSTR-2B but not in the books">
          <Table rows={w.unclaimed} rowKey={(r) => `${r.supplier_gstin}-${r.number}`} columns={[
            { key: "s", header: "Supplier", render: (r) => r.supplier_name ?? r.supplier_gstin },
            { key: "n", header: "Invoice", render: (r) => r.number },
            { key: "d", header: "Date", render: (r) => r.date ?? "—" },
            { key: "i", header: "ITC", render: (r) => inr(r.itc) },
          ]} />
        </Card>
      ) : null}
      {w.mismatches.length ? (
        <Card title="Amounts that differ from GSTR-2B">
          <Table rows={w.mismatches} rowKey={(r) => r.number} columns={[
            { key: "n", header: "Invoice", render: (r) => r.number },
            { key: "b", header: "Your records", render: (r) => inr(r.value) },
            { key: "g", header: "In 2B", render: (r) => inr(r.value_2b) },
          ]} />
        </Card>
      ) : null}
    </div>
  );
}
